import { ContentDialog } from "../../../components/MDDForm/components/ContentDialog.jsx";
import { Box, Paper, Typography } from "@mui/material";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import ClaimInfo from "../../../components/ClaimInfo.jsx";
import ClaimBookingsTable from "../../../components/tables/ClaimBookingsTable.jsx";
import { borderRadius, colorsFoundation } from "@accredo/fe-shared/theme/index.js";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import { OpenInNew } from "@mui/icons-material";
import PropTypes from "prop-types";

const ClaimInfoModal = ({
  open,
  claim,
  claimBookings = [],
  onClose,
  isLoading,
}) => {
  return (
    <ContentDialog
      openCD={open}
      setOpenCD={onClose}
      tca={{
        title: claim
          ? `Schadendetails - ${claim.schaden_nummer_accredo}`
          : "Schadendetails",
        content: isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <Spinner size={32} />
          </Box>
        ) : claim ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Claim Overview using ClaimInfo */}
            <ClaimInfo claim={claim} readOnly={true} showContent={true} />

            {/* Bookings Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Buchungen ({claimBookings.length})
              </Typography>

              {claimBookings.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  Keine Buchungen vorhanden
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {/* Incoming */}
                  <Paper elevation={0} sx={{}}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: "success.main",
                      }}
                    >
                      Zahlungseingänge (
                      {claimBookings.filter((b) => b.type === "IN").length})
                    </Typography>
                    <ClaimBookingsTable
                      schaden_id={claim.id}
                      compact={true}
                      bookings={claimBookings.filter((b) => b.type === "IN")}
                    />
                  </Paper>

                  {/* Outgoing */}
                  <Paper elevation={0}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: "error.main",
                      }}
                    >
                      Zahlungsausgänge (
                      {claimBookings.filter((b) => b.type === "OUT").length})
                    </Typography>
                    <ClaimBookingsTable
                      schaden_id={claim.id}
                      compact={true}
                      bookings={claimBookings.filter((b) => b.type === "OUT")}
                    />
                  </Paper>
                </Box>
              )}

              {/* Totals */}
              {claimBookings.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: borderRadius.md,
                    backgroundColor: colorsFoundation.neutral[50],
                    border: `1px solid ${colorsFoundation.neutral[200]}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Differenz (Eingänge - Ausgänge)
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color:
                          claimBookings.reduce((acc, b) => {
                            const value = parseFloat(
                              String(b.amount).replace(",", ".") || 0
                            );
                            return acc + (b.type === "OUT" ? -value : value);
                          }, 0) >= 0
                            ? "success.main"
                            : "error.main",
                      }}
                    >
                      {claimBookings
                        .reduce((acc, b) => {
                          const value = parseFloat(
                            String(b.amount).replace(",", ".") || 0
                          );
                          return acc + (b.type === "OUT" ? -value : value);
                        }, 0)
                        .toLocaleString("de-DE", {
                          style: "currency",
                          currency: "EUR",
                        })}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Actions moved to dialog actions */}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Keine Daten verfügbar
          </Typography>
        ),
        actions: claim ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              width: "100%",
            }}
          >
            <DsButton variant="tertiary" onClick={onClose}>
              Schließen
            </DsButton>
            <DsButton
              variant="primary"
              onClick={() => window.open(`/schaden/${claim.id}`, "_blank")}
              endIcon={<OpenInNew fontSize="small" />}
            >
              Schaden öffnen
            </DsButton>
          </Box>
        ) : null,
      }}
      maxWidth="lg"
    />
  );
};

ClaimInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  claim: PropTypes.object,
  claimBookings: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default ClaimInfoModal;
